import { useMemo, useState } from "react";
import { Box, Button, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, InputAdornment, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import TuneIcon from "@mui/icons-material/Tune";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FrostedCard from "src/components/quote/FrostedCard";
import { captureError } from "src/utils/errorReporting";
import { filterAndSortRows } from "src/utils/hallinta/entityListFilter";
import { useT } from "src/i18n";

// Lists shorter than this don't get the search/sort toggle (nothing to find).
const CONTROLS_MIN_ROWS = 5;

// Header buttons (Suodata / Lisää) are icon-only on phones and gain their text
// label from the `sm` breakpoint up, so a narrow header keeps room for the title.
// aria-label is set on the buttons so the icon-only state stays accessible.
const COMPACT_BTN_SX = {
  minWidth: { xs: 40, sm: "auto" },
  px: { xs: 1, sm: 1.5 },
  "& .MuiButton-startIcon": { mr: { xs: 0, sm: 1 }, ml: { xs: 0, sm: -0.5 } },
};
const COMPACT_LABEL_SX = { display: { xs: "none", sm: "inline" } };

// Props:
//  title, rows, loading, error, canEdit,
//  renderRow(row) -> ReactNode (primary line + secondary lines),
//  onAdd(), onEdit(row), onDelete(row) -> Promise,
//  entityTag (string, used for Sentry tag; default "entity")
//  headerExtra            -> ReactNode rendered in the header (e.g. a filter toggle)
//  deleteConfig           -> overrides the destructive-action copy/icon (see `dc` below)
//  canDelete(row) -> bool -> hide the destructive button per-row (default: always show)
//  renderExtraActions(row)-> ReactNode appended to a row's action cluster (e.g. reactivate)
//  searchKeys (string[])  -> row keys the free-text box matches against (omit = no search)
//  sortOptions ({key,label}[]) -> selectable sort fields (omit = no sort); "" = incoming order
export default function EntityList({ title, rows, loading, error, canEdit, renderRow, onAdd, onEdit, onDelete, entityTag = "entity", headerExtra, deleteConfig, canDelete = () => true, renderExtraActions, searchKeys = [], sortOptions = [] }) {
  const t = useT();
  const [confirm, setConfirm] = useState(null); // row pending delete
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState(""); // "" = default (incoming) order
  const [sortDir, setSortDir] = useState("asc");

  // Only offer controls when there's something to search/sort AND enough rows
  // to justify the chrome. Threshold is on the total list, not the filtered view.
  const hasControls = (searchKeys.length > 0 || sortOptions.length > 0) && (rows?.length ?? 0) > CONTROLS_MIN_ROWS;
  const filterActive = !!search || !!sortKey;

  const viewRows = useMemo(
    () => (hasControls ? filterAndSortRows(rows, { search, searchKeys, sortKey, sortDir }) : rows),
    [hasControls, rows, search, searchKeys, sortKey, sortDir]
  );

  const dc = {
    ariaLabel: t("provider.entityList.deleteLabel"),
    icon: <DeleteIcon fontSize="small" />,
    confirmTitle: t("provider.entityList.confirmDeleteTitle"),
    confirmBody: t("provider.entityList.confirmDeleteBody"),
    confirmActionLabel: t("provider.entityList.deleteLabel"),
    failLabel: t("provider.entityList.deleteFailed"),
    color: "error",
    ...deleteConfig,
  };

  async function doDelete() {
    setDeleting(true);
    setDeleteError(null);
    try {
      await onDelete(confirm);
      setConfirm(null);
    } catch (err) {
      captureError(err, { tags: { entity: entityTag, operation: "delete" } });
      setDeleteError(err?.message || dc.failLabel);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", mb: 2, gap: 1, flexWrap: "wrap" }}>
        <Typography sx={{ fontFamily: "'Black Ops One', serif", fontSize: 16, letterSpacing: "0.06em", textTransform: "uppercase", color: "text.primary" }}>
          {title}
        </Typography>
        <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
          {headerExtra}
          {hasControls && (
            <Button size="small" variant="outlined" color={filterActive ? "primary" : "inherit"}
              startIcon={<TuneIcon />} onClick={() => setFilterOpen((o) => !o)} aria-expanded={filterOpen}
              aria-label={t("provider.entityList.filter")} sx={COMPACT_BTN_SX}>
              <Box component="span" sx={COMPACT_LABEL_SX}>{t("provider.entityList.filter")}</Box>
            </Button>
          )}
          {canEdit && (
            <Button startIcon={<AddIcon />} variant="contained" size="small" onClick={onAdd}
              aria-label={t("provider.entityList.add")} sx={COMPACT_BTN_SX}>
              <Box component="span" sx={COMPACT_LABEL_SX}>{t("provider.entityList.add")}</Box>
            </Button>
          )}
        </Stack>
      </Stack>

      {hasControls && (
        <Collapse in={filterOpen} unmountOnExit>
          <Box sx={{ mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
            <Stack spacing={1.5}>
              {searchKeys.length > 0 && (
                <TextField
                  size="small" fullWidth value={search} placeholder={t("provider.entityList.searchPlaceholder")}
                  onChange={(e) => setSearch(e.target.value)}
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton size="small" aria-label={t("provider.entityList.clearSearchAria")} onClick={() => setSearch("")}>
                            <ClearIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    },
                  }}
                />
              )}
              {sortOptions.length > 0 && (
                <Stack direction="row" sx={{ alignItems: "center", gap: 1 }}>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("provider.entityList.sortByLabel")}</Typography>
                  <Select size="small" value={sortKey} onChange={(e) => setSortKey(e.target.value)} sx={{ minWidth: 160 }}>
                    <MenuItem value="">{t("provider.entityList.sortDefault")}</MenuItem>
                    {sortOptions.map((o) => <MenuItem key={o.key} value={o.key}>{o.label}</MenuItem>)}
                  </Select>
                  <IconButton size="small" aria-label={t("provider.entityList.changeSortDirAria")} disabled={!sortKey}
                    onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}>
                    {sortDir === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
                  </IconButton>
                </Stack>
              )}
            </Stack>
          </Box>
        </Collapse>
      )}

      {loading && <Typography variant="body2" sx={{ color: "text.secondary" }}>{t("common.loading")}</Typography>}
      {error && <Typography variant="body2" color="error">{t("provider.entityList.loadFailed")}</Typography>}
      {!loading && !error && (viewRows?.length ? (
        <Stack spacing={1.5}>
          {viewRows.map((row, i) => (
            <FrostedCard key={row.__key ?? i} sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <Box sx={{ minWidth: 0 }}>{renderRow(row)}</Box>
              {canEdit && (
                <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <IconButton size="small" aria-label={t("provider.shared.edit")} onClick={() => onEdit(row)}><EditIcon fontSize="small" /></IconButton>
                  {canDelete(row) && (
                    <IconButton size="small" aria-label={dc.ariaLabel} onClick={() => setConfirm(row)}>{dc.icon}</IconButton>
                  )}
                  {renderExtraActions?.(row)}
                </Stack>
              )}
            </FrostedCard>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {search ? t("provider.entityList.noSearchResults") : t("provider.entityList.noData")}
        </Typography>
      ))}

      <Dialog open={!!confirm} onClose={deleting ? undefined : () => { setConfirm(null); setDeleteError(null); }}>
        <DialogTitle>{dc.confirmTitle}</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{dc.confirmBody}</Typography>
          {deleteError && <Typography variant="caption" color="error" sx={{ display: "block", mt: 1 }}>{deleteError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setConfirm(null); setDeleteError(null); }} disabled={deleting}>{t("provider.shared.cancel")}</Button>
          <Button color={dc.color} onClick={doDelete} disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : null}>{dc.confirmActionLabel}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
